import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from "@angular/core";

import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { ButtonModule } from "primeng/button";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import {
	ContentSlideComponent,
	ContentSliderComponent,
	ContentSliderService,
	contentSliderServiceInjectionToken,
	ContentSliderStateService,
	contentSliderStateServiceInjectionToken,
	CountrySelectComponent,
	countryFromBrowser,
	ESeverity,
	IContentSliderService,
	IContentSliderStateService,
	IInputTextOptions,
	IToastService,
	InputTextComponent,
	toastServiceInjectionToken
} from "@zaeper/angular-dashboard-view-lib";
import { companyRestClientInjectionToken } from "../../rest-clients/company/company-rest-client-injection-token";
import { ICompanyRestClient } from "../../rest-clients/company/i-company-rest.client";
import { ZDictionaryPipe } from "@zaeper/angular-localization-lib";
import { WelcomeWizardInitialState } from "./i-welcome-wizard-modal.service";

interface IWelcomeWizardModalData {
	initialState: WelcomeWizardInitialState;
}

@Component({
	selector: "app-welcome-wizard-modal",
	templateUrl: "./welcome-wizard-modal.component.html",
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
    ReactiveFormsModule,
    ButtonModule,
    ContentSliderComponent,
    ContentSlideComponent,
    InputTextComponent,
    CountrySelectComponent,
    ZDictionaryPipe
],
	providers: [
		{
			provide: contentSliderStateServiceInjectionToken,
			useClass: ContentSliderStateService
		}, {
			provide: contentSliderServiceInjectionToken,
			useClass: ContentSliderService
		}
	]
})
export class WelcomeWizardModalComponent implements OnInit, OnDestroy {
	public isSubmitting: boolean = false;
	public selectedMode: "create" | "join" | null = null;
	public modalTitle: string = "wizard-welcome-title";
	public showBackButton: boolean = false;

	public readonly createCompanyForm: FormGroup;
	public readonly joinCompanyForm: FormGroup;

	public readonly nameInputOptions: IInputTextOptions;
	public readonly shortcodeInputOptions: IInputTextOptions;
	public readonly descriptionInputOptions: IInputTextOptions;
	public readonly joinCodeInputOptions: IInputTextOptions;

	private _pollingIntervalId: ReturnType<typeof setInterval> | null = null;

	constructor(
		@Inject(contentSliderServiceInjectionToken) private readonly _contentSliderService: IContentSliderService,
		@Inject(contentSliderStateServiceInjectionToken) private readonly _contentSliderStateService: IContentSliderStateService,
		@Inject(companyRestClientInjectionToken) private readonly _companyRestClient: ICompanyRestClient,
		@Inject(toastServiceInjectionToken) private readonly _toastService: IToastService,
		private readonly _dynamicDialogRef: DynamicDialogRef,
		private readonly _dynamicDialogConfig: DynamicDialogConfig<IWelcomeWizardModalData>,
		private readonly _changeDetectorRef: ChangeDetectorRef
	) {
		this.createCompanyForm = new FormGroup({
			name: new FormControl("", [Validators.required]),
			shortcode: new FormControl("", [Validators.required]),
			description: new FormControl(""),
			// The billing country, preset from the browser language; it decides the company's currency.
			country: new FormControl<string | null>(countryFromBrowser(), [Validators.required])
		});

		this.joinCompanyForm = new FormGroup({
			joinCode: new FormControl("", [Validators.required])
		});

		this.nameInputOptions = {
			title: "Name",
			inputId: "company-name",
			placeholder: "Name der Organisation",
			required: true,
			formControlEntry: {
				formControl: this.createCompanyForm.controls["name"] as FormControl<string>,
				key: "name"
			}
		};

		this.shortcodeInputOptions = {
			title: "Kürzel",
			inputId: "company-shortcode",
			placeholder: "z.B. ACME",
			required: true,
			formControlEntry: {
				formControl: this.createCompanyForm.controls["shortcode"] as FormControl<string>,
				key: "shortcode"
			}
		};

		this.descriptionInputOptions = {
			title: "Beschreibung",
			inputId: "company-description",
			placeholder: "Optionale Beschreibung",
			required: false,
			formControlEntry: {
				formControl: this.createCompanyForm.controls["description"] as FormControl<string>,
				key: "description"
			}
		};

		this.joinCodeInputOptions = {
			title: "Beitrittscode",
			inputId: "join-code",
			placeholder: "z.B. ACME-abc123",
			required: true,
			formControlEntry: {
				formControl: this.joinCompanyForm.controls["joinCode"] as FormControl<string>,
				key: "joinCode"
			}
		};
	}

	public ngOnInit(): void {
		this._contentSliderStateService.activeSlideIndex$.subscribe((index) => {
			this.showBackButton = index > 0 && index < 2;
			if (index === 0) {
				this.modalTitle = "wizard-welcome-title";
			}
			this._changeDetectorRef.markForCheck();
		});

		const initialState = this._dynamicDialogConfig.data?.initialState ?? "choose";
		if (initialState === "pending") {
			setTimeout(() => {
				this._contentSliderService.nextSlide();
				this._contentSliderService.nextSlide();
				this.modalTitle = "wizard-pending-title";
				this._startPolling();
				this._changeDetectorRef.markForCheck();
			});
		}
	}

	public ngOnDestroy(): void {
		this._stopPolling();
	}

	public selectCreate(): void {
		this.selectedMode = "create";
		this.modalTitle = "wizard-create-title";
		this._changeDetectorRef.markForCheck();
		this._contentSliderService.nextSlide();
	}

	public selectJoin(): void {
		this.selectedMode = "join";
		this.modalTitle = "wizard-join-title";
		this._changeDetectorRef.markForCheck();
		this._contentSliderService.nextSlide();
	}

	public goBack(): void {
		this._contentSliderService.prevSlide();
	}

	public submitCreateCompany(event: Event): void {
		event.preventDefault();
		if (!this.createCompanyForm.valid || this.isSubmitting) return;

		this.isSubmitting = true;
		this._changeDetectorRef.markForCheck();

		this._companyRestClient.createCompany({
			name: this.createCompanyForm.get("name")?.value,
			shortcode: this.createCompanyForm.get("shortcode")?.value,
			description: this.createCompanyForm.get("description")?.value ?? "",
			country: this.createCompanyForm.get("country")?.value
		}).then(() => {
			this._toastService.setMessage({
				title: "wizard-success",
				detail: "wizard-create-success",
				severity: ESeverity.success
			});
			this._dynamicDialogRef.close(true);
		}).catch(() => {
			this.isSubmitting = false;
			this._changeDetectorRef.markForCheck();
			this._toastService.setMessage({
				title: "wizard-error",
				detail: "wizard-create-error",
				severity: ESeverity.error
			});
		});
	}

	public submitJoinRequest(event: Event): void {
		event.preventDefault();
		if (!this.joinCompanyForm.valid || this.isSubmitting) return;

		this.isSubmitting = true;
		this._changeDetectorRef.markForCheck();

		this._companyRestClient.submitJoinRequest({
			joinCode: this.joinCompanyForm.get("joinCode")?.value
		}).then(() => {
			this.isSubmitting = false;
			this.modalTitle = "wizard-pending-title";
			this._changeDetectorRef.markForCheck();
			this._contentSliderService.nextSlide();
			this._startPolling();
		}).catch(() => {
			this.isSubmitting = false;
			this._changeDetectorRef.markForCheck();
			this._toastService.setMessage({
				title: "wizard-error",
				detail: "wizard-join-error",
				severity: ESeverity.error
			});
		});
	}

	private _startPolling(): void {
		this._stopPolling();
		this._pollingIntervalId = setInterval(() => {
			this._checkJoinRequestStatus();
		}, 5000);
	}

	private _stopPolling(): void {
		if (this._pollingIntervalId !== null) {
			clearInterval(this._pollingIntervalId);
			this._pollingIntervalId = null;
		}
	}

	private async _checkJoinRequestStatus(): Promise<void> {
		try {
			const pendingRequest = await this._companyRestClient.getMyPendingJoinRequest();

			if (pendingRequest === null) {
				this._stopPolling();

				const companyPage = await this._companyRestClient.getCompanyPage({ pageNumber: 0, pageSize: 1 });

				if (companyPage.records.length > 0) {
					this._toastService.setMessage({
						title: "wizard-success",
						detail: "wizard-join-success",
						severity: ESeverity.success
					});
					this._dynamicDialogRef.close(true);
				} else {
					this._toastService.setMessage({
						title: "wizard-error",
						detail: "wizard-join-rejected",
						severity: ESeverity.error
					});
					this.selectedMode = null;
					this.modalTitle = "wizard-welcome-title";
					this._contentSliderService.prevSlide();
					this._contentSliderService.prevSlide();
					this._changeDetectorRef.markForCheck();
				}
			}
		} catch {
			// Polling error — will retry on next interval
		}
	}
}
